import { blessRouterABI } from "@/config/abis/bless-router-abi";
import { BRIDGE_ABI } from "@/config/abis/bridge-abi";
import { PRECOMPILER_ABI } from "@/config/abis/precompiler-abi";
import { config } from "@/config/rainbow.config";
import {
  ChainData,
} from "@/constants/chain-details";
import {
  ARBITRUM_CHAIN_ID,
  ARBITRUM_WORMHOLE,
  ARBSYS,
  BLESSNET_CHAIN_ID,
  GAS_LIMIT,
  L2_DATA,
  L2_GAS_LIMIT,
  L2_MAX_FEE_PER_GAS,
  L2_MAX_SUBMISSION_COST,
} from "@/features/bridge/constants/bridge.constant";
import quoteSelfBridgeOut from "@/features/bridge/contract/quoteSelfBridge";
import {
  BridgeControlsType,
  ValidationManagerType,
  type GeneralAssetMethodsType,
} from "@/features/bridge/providers/bridge.provider";
import { CheckoutStatusType } from "@/features/bridge/types/checkout.types";
import type { SelectedMultiTokenData } from "@/features/bridge/types/method-functions.types";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { errorLog, log } from "@/features/bridge/utils/log";

import { ethers, parseEther, parseUnits } from "ethers";
import { useSwitchChain, useWriteContract } from "wagmi";
import { waitForTransactionReceipt } from "wagmi/actions";
import {
  showToast,
  showToastTxRevert,
  showToastTxSuccess,
} from "@components/layout/toast/show-toast";
import { TOAST_TX_MSG } from "@/features/bridge/constants/toast-msgs.constants";
import { isMainnet } from "@/constants/flags.constant";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import type { HexString } from "@/types/general.types";
import { useManagedWriteContract } from "@/hooks/useManagedContractWrite.hook";
import { getExecutorSignedQuote } from "@/features/bridge/services/wormhole/getExecutorSignedQuote";
import { encodeGasRelayInstruction } from "@/features/bridge/services/wormhole/relayInstructions";

const useContractMethods = (
  bridgeControls: BridgeControlsType,
  validationManager: ValidationManagerType,
  generalAssetMethods: GeneralAssetMethodsType,
) => {
  const isQuoteExpiredError = (error: unknown) =>
    error instanceof Error && /expired|expiry/i.test(error.message);
  const getReadableQuoteErrorMessage = (error: unknown) =>
    error instanceof Error && error.message
      ? error.message
      : TOAST_TX_MSG.EXECUTOR_QUOTE_ERROR.toastContent.message;

  const getExecutorCostQuote = async (
    srcWormholeId: number | null | undefined,
    dstWormholeId: number | null | undefined,
    gasLimit: bigint,
    msgValue: bigint = BigInt(0),
  ) => {
    if (!srcWormholeId || !dstWormholeId) {
      throw new Error("Missing source or destination wormhole id");
    }

    try {
      const relayInstructions = encodeGasRelayInstruction(gasLimit, msgValue);
      const quote = await getExecutorSignedQuote({
        srcChain: srcWormholeId,
        dstChain: dstWormholeId,
        relayInstructions,
      });
      return quote.estimatedCost;
    } catch (error) {
      if (isQuoteExpiredError(error)) {
        showToast(TOAST_TX_MSG.EXECUTOR_QUOTE_EXPIRED);
      } else {
        console.error("Executor quote failed while preparing bridge transaction", {
          srcWormholeId,
          dstWormholeId,
          gasLimit: gasLimit.toString(),
          msgValue: msgValue.toString(),
          error,
        });
        showToast({
          toastContent: {
            ...TOAST_TX_MSG.EXECUTOR_QUOTE_ERROR.toastContent,
            message: getReadableQuoteErrorMessage(error),
          },
        });
      }
      throw error;
    }
  };

  const {
    toChain: { destinationChain },
    fromChain: { originChain },
    account,
    setAmount,
    setIsBridgePending,
    setCheckoutLoadingStatus,
    pendingTxns,
    setPendingTxns,
  } = bridgeControls;

  const {
    isInputParamsValid,
    canBridgeOut,
    canBridgeOutToNative,
    isArbToBless,
    isNetworkChainMatchSelected,
    isDestinationChainBless,
    isOriginChainBless,
  } = validationManager;

  const {
    holdAndShiftOut20,
    holdAndShiftOut721,
    holdAndShiftOut1155,
    shiftOutShiftableERC20,
    shiftOutShiftableERC721,
    shiftOutShiftableERC1155,
    resetApprovalForMultiTokens,
  } = generalAssetMethods;

  const { switchChainAsync } = useSwitchChain({
    mutation: {
      onError: (error) => {
        setIsBridgePending(false);
        errorLog(error);
      },
      onMutate: () => log("Switching network..."),
    },
  });

  const { writeContractAsyncWithTimeout } = useManagedWriteContract({
    mainnetOptimized: isMainnet, // Enable enhanced mainnet compatibility
    callback: {
      onSigningError: () => {
        showToast(TOAST_TX_MSG.SIGN_ERROR);
      },
      onTimeout: () => {
        setAmount("");
        showToast({
          toastContent: TOAST_TX_MSG.TX_SUCCESS_TIMEOUT.toastContent,
        });
      },
      onRevert: showToastTxRevert,
      onSuccess: (receiptHash) => {
        setAmount("");
        showToastTxSuccess(receiptHash);
      },
    },
  });

  //? BRIDGE OUT FROM BLESS
  /**
   * Initiates the bridge process from the BLESS network.
   *
   * This function first validates all necessary parameters. If the selected network chain
   * does not match the `BLESSNET_CHAIN_ID`, it attempts to switch the chain. If the switch fails,
   * it displays a generic error toast and logs the error.
   *
   * Once the network is confirmed, the function encodes the call data needed for a withdrawal
   * transaction and sends it to the Layer 1 network using `sendTxToL1`. It also handles the
   * state of the bridge process by setting `setIsBridgePending` and logs the transaction hash
   * upon successful transmission. The function fetches updated balances after the transaction is sent.
   *
   * @async
   * @function bridgeFromBless
   * @returns {Promise<HexString|undefined>} Returns the transaction response hash if the transaction
   * is successfully sent, otherwise returns `undefined` if there is an error.
   *
   * @throws {Error} If an error occurs during the network switch or the transaction, the error is logged,
   * and a generic error toast is shown.
   */
  async function bridgeFromBless(
    dChain: ChainData | undefined,
    decimalAmount: string,
    shouldValidate: boolean = true,
  ): Promise<HexString | undefined> {
    if (shouldValidate) {
      if (!isInputParamsValid(TokenType.STANDARD)) return;
    }

    if (!isNetworkChainMatchSelected(BLESSNET_CHAIN_ID)) {
      try {
        const response = await switchChainAsync({
          chainId: BLESSNET_CHAIN_ID,
        });
        if (!response) return;
      } catch (error) {
        errorLog(error);
      }
    }

    const blessRouterInterface = new ethers.Interface(blessRouterABI);

    const callDataForL1 = blessRouterInterface.encodeFunctionData(
      "routeWithdrawal",
      [
        dChain?.wormholeId,
        parseEther(decimalAmount),
        GAS_LIMIT,
        account.address,
      ],
    ) as HexString;

    await getExecutorCostQuote(
      originChain?.wormholeId,
      dChain?.wormholeId,
      GAS_LIMIT,
    );

    log("Calling routeWithdrawal method!");
    const response = await writeContractAsyncWithTimeout({
      abi: PRECOMPILER_ABI,
      address: ARBSYS,
      functionName: "sendTxToL1",
      args: [CONTRACT_ADDRESSES.ROUTER_ADDRESS, callDataForL1],
      value: parseEther(decimalAmount),
    });
    return response;
  }

  //? TRANSFER AND CALL METHOD
  /**
   * Executes a transfer and call operation from Arbitrum to Blessnet.
   *
   * This function first validates all parameters and ensures that the origin network is Arbitrum.
   * If the network chain does not match `ARBITRUM_CHAIN_ID`, it attempts to switch the chain.
   * Upon successful network validation, it prepares the necessary data and sends a `transferAndCall`
   * transaction to the Blessnet contract.
   *
   * @async
   * @function transferAndCall
   * @returns {Promise<HexString|undefined>} Returns the transaction response hash if the transaction
   * is successfully sent, or `undefined` if there is an error.
   *
   * @throws {Error} Logs the error and displays a generic error toast if the transaction fails.
   */
  async function transferAndCall(
    decimalAmount: string,
  ): Promise<HexString | undefined> {
    if (!isNetworkChainMatchSelected(ARBITRUM_CHAIN_ID)) {
      const response = await switchChainAsync({
        chainId: ARBITRUM_CHAIN_ID,
      });

      if (!response) {
        showToast(TOAST_TX_MSG.SIGN_ERROR);
        return;
      }
    }

    log("Calling transferAndCall method!");
    const l2CallValue =
      parseEther(decimalAmount) -
      BigInt(Number(L2_GAS_LIMIT) * Number(L2_MAX_FEE_PER_GAS));
    const request = ethers.AbiCoder.defaultAbiCoder().encode(
      [
        "address", // to
        "uint256", // gasLimit
        "uint256", // l2CallValue
        "uint256", // L2MaxSubmissionCost
        "uint256", // L2GasLimit
        "uint256", // l2MaxFeePerGas
      ],
      [
        account.address,
        GAS_LIMIT,
        l2CallValue,
        L2_MAX_SUBMISSION_COST,
        L2_GAS_LIMIT,
        L2_MAX_FEE_PER_GAS,
      ],
    ) as HexString;
    const response = await writeContractAsyncWithTimeout({
      abi: BRIDGE_ABI,
      address: CONTRACT_ADDRESSES.BLESS_TOKEN,
      functionName: "transferAndCall",
      args: [
        CONTRACT_ADDRESSES.ROUTER_ADDRESS,
        parseEther(decimalAmount),
        request,
      ],
    });

    return response;
  }

  //? NATIVE BRIDGE OUT METHOD
  /**
   * Initiates the process of bridging tokens out from the origin chain to the blessnet chain.
   *
   * This function first validates all necessary parameters and ensures that the bridge out operation
   * can proceed to the native chain. If the selected network chain does not match the current chain network,
   * it attempts to switch to the correct chain. Upon successful network validation, it calculates the
   * required values and quotes, and then sends the `selfBridgeOutToNative` transaction to the Blessnet contract.
   *
   * @async
   * @function bridgeOutNative
   * @returns {Promise<HexString|undefined>} Returns the transaction response hash if the transaction
   * is successfully sent, or `undefined` if there is an error.
   *
   * @throws {Error} Logs the error and displays a generic error toast if the transaction fails.
   */
  async function bridgeOutNative(
    oChain: ChainData | undefined,
    dChain: ChainData | undefined,
    decimalAmount: string,
    shouldValidate: boolean = true,
  ): Promise<HexString | undefined> {
    if (shouldValidate) {
      if (!isInputParamsValid(TokenType.STANDARD) && !canBridgeOutToNative())
        return;
    }

    if (!isNetworkChainMatchSelected()) {
      const response = await switchChainAsync({
        chainId: oChain!.chainId,
      });
      if (!response) {
        showToast(TOAST_TX_MSG.SIGN_ERROR);
        return;
      }
    }

    log("Calling bridgeOutToNative method!");

    const l2CallValue =
      parseEther(decimalAmount) - L2_GAS_LIMIT * L2_MAX_FEE_PER_GAS;
    const nativeQuote = await quoteSelfBridgeOut(
      oChain?.chainId,
      dChain?.chainId,
    );
    const executorCost = await getExecutorCostQuote(
      oChain?.wormholeId,
      dChain?.wormholeId,
      GAS_LIMIT,
      l2CallValue,
    );
    const txValue = nativeQuote
      ? nativeQuote > executorCost
        ? nativeQuote
        : executorCost
      : executorCost;
    const response = await writeContractAsyncWithTimeout({
      abi: BRIDGE_ABI,
      address: CONTRACT_ADDRESSES.BLESS_TOKEN,
      functionName: "selfBridgeOutToNative",
      args: [
        ARBITRUM_WORMHOLE,
        parseEther(decimalAmount),
        GAS_LIMIT,
        l2CallValue,
        BigInt(L2_MAX_SUBMISSION_COST),
        BigInt(L2_GAS_LIMIT),
        BigInt(L2_MAX_FEE_PER_GAS),
        L2_DATA,
      ],
      value: txValue,
    });
    return response;
  }

  //? BRIDGEOUT METHOD
  /**
   * Initiates the standard bridging process to another chain where the origin or destination is not blessnet.
   *
   * This function validates all necessary parameters before proceeding. If the network chain does
   * not match the expected chain, it attempts to switch to the correct chain. Upon successful validation,
   * it retrieves a quote for the bridge operation and sends a `selfBridgeOut` transaction to the Blessnet contract.
   *
   * @async
   * @function bridgeOut
   * @returns {Promise<HexString | undefined >} Returns the transaction response hash if the transaction
   * is successfully sent, or `undefined` if there is an error.
   *
   * @throws {Error} Logs the error and displays a generic error toast if the transaction fails.
   */
  async function bridgeOut(
    oChain: ChainData | undefined,
    dChain: ChainData | undefined,
    decimalAmount: string,
    shouldValidate: boolean = true,
  ): Promise<HexString | undefined> {
    if (shouldValidate) {
      if (!isInputParamsValid(TokenType.STANDARD)) return;
      if (!canBridgeOut()) return;
    }

    if (!isNetworkChainMatchSelected()) {
      const response = await switchChainAsync({
        chainId: oChain!.chainId,
      });

      if (!response) {
        showToast(TOAST_TX_MSG.SIGN_ERROR);
        return undefined;
      }
    }

    log("Calling bridgeOut method!");

    const nativeQuote = await quoteSelfBridgeOut(
      oChain?.chainId,
      dChain?.chainId,
    );
    const executorCost = await getExecutorCostQuote(
      oChain?.wormholeId,
      dChain?.wormholeId,
      GAS_LIMIT,
    );
    const txValue = nativeQuote
      ? nativeQuote > executorCost
        ? nativeQuote
        : executorCost
      : executorCost;
    const response = await writeContractAsyncWithTimeout({
      abi: BRIDGE_ABI,
      address: CONTRACT_ADDRESSES.BLESS_TOKEN,
      functionName: "selfBridgeOut",
      args: [dChain?.wormholeId!, parseEther(decimalAmount), GAS_LIMIT],
      value: txValue,
    });
    return response;
  }

  //? HANDLE SELECT BRIDGING METHOD
  /**
   * Handles the bridging of tokens between different chains based on the origin and destination chains.
   *
   * This function determines the appropriate method to use for bridging tokens depending on the origin
   * and destination chains. It calls the corresponding method (`transferAndCall`, `bridgeOutNative`,
   * `bridgeFromBless`, or `bridgeOut`) and logs the operation. If any error occurs, it throws a new error.
   *
   * @async
   * @function bridgeTokens
   * @returns {Promise<HexString | void>} Resolves with the response from the specific
   * bridging method or `void` if there’s no response.
   *
   * @throws {Error} Throws a new error if any error is caught during the bridging process.
   */
  async function bridgeTokens(tokenAmount: string): Promise<HexString | void> {
    try {
      if (isArbToBless()) {
        return await transferAndCall(tokenAmount);
      }
      if (isDestinationChainBless()) {
        return await bridgeOutNative(
          originChain,
          destinationChain,
          tokenAmount,
        );
      }
      if (isOriginChainBless()) {
        return await bridgeFromBless(destinationChain, tokenAmount);
      } else {
        return await bridgeOut(originChain, destinationChain, tokenAmount);
      }
    } catch (error: any) {
      errorLog(error);
      return;
    }
  }

  //$ CHECKOUT - ERC20
  async function checkoutForERC20(
    destChain: ChainData | undefined,
    tokenContractAddress: HexString | undefined,
    amountToBridge: number | undefined,
    decimals?: number,
  ): Promise<boolean> {
    if (
      !destChain ||
      !amountToBridge ||
      !tokenContractAddress ||
      !account.address
    ) {
      errorLog(
        "Missing either destination chain, amount to bridge, token address or wallet address",
      );
      return false;
    }

    const tokenToWeiAmount = parseUnits(amountToBridge.toString(), decimals);

    const shouldSendToBlessnet = isDestinationChainBless();

    log("Sending to blessnet? => ", shouldSendToBlessnet);

    if (!isNetworkChainMatchSelected() && originChain) {
      await switchChainAsync({
        chainId: originChain.chainId,
      });
    }

    const checkedChainId = destChain;

    setCheckoutLoadingStatus(CheckoutStatusType.CheckingOut);
    try {
      log("Starting token shiftOut bridge flow...");
      setCheckoutLoadingStatus(CheckoutStatusType.BridgingOut);
      const shiftOut = await shiftOutShiftableERC20(
        checkedChainId,
        tokenContractAddress,
        tokenToWeiAmount,
        shouldSendToBlessnet,
      );
      if (shiftOut) return true;
      return false;
    } catch (error) {
      errorLog(error);
    } finally {
      setCheckoutLoadingStatus(null);
    }
    return false;
  }

  //$ CHECKOUT - ERC721
  async function checkoutForERC721(
    destChain: ChainData | undefined,
    tokenContractAddress: HexString | undefined,
    tokenIds: bigint[],
  ) {
    if (!destChain || !tokenContractAddress || !account.address)
      return errorLog(
        "There is either no destination chain, or amount to bridge, or token address",
      );

    const shouldSendToBlessnet = isDestinationChainBless();

    if (!isNetworkChainMatchSelected() && originChain) {
      await switchChainAsync({
        chainId: originChain.chainId,
      });
    }

    const checkedChainId = destChain;

    log("Shifting out tokens => ", tokenIds);

    // Set the loading state
    setCheckoutLoadingStatus(CheckoutStatusType.CheckingOut);
    try {
      log("Shifting out...");
      setCheckoutLoadingStatus(CheckoutStatusType.BridgingOut);
      const txRes = await shiftOutShiftableERC721(
        checkedChainId,
        tokenContractAddress,
        tokenIds,
        shouldSendToBlessnet,
      );
      if (txRes) return true;
    } catch (error) {
      errorLog(error);
      return false;
    } finally {
      setCheckoutLoadingStatus(null);
    }
  }

  //$ CHECKOUT - ERC1155
  async function checkoutForERC1155(
    destChain: ChainData | undefined,
    tokenContractAddress: HexString | undefined,
    selectedTokens: SelectedMultiTokenData[],
  ) {
    if (!destChain || !tokenContractAddress || !account.address)
      return errorLog(
        "There is either no destination chain, or amount to bridge, or token address",
      );

    const shouldSendToBlessnet = isDestinationChainBless();

    if (!isNetworkChainMatchSelected() && originChain) {
      await switchChainAsync({
        chainId: originChain.chainId,
      });
    }

    const checkedChainId = destChain;

    //? Id and amount index should match
    const tokenIds = selectedTokens.map((token) => BigInt(token.id));
    const tokenAmounts = selectedTokens.map((token) => BigInt(token.amount));

    log("Shifting out tokens => ", selectedTokens);

    try {
      log("Shifting out...");
      setCheckoutLoadingStatus(CheckoutStatusType.BridgingOut);
      const txRes = await shiftOutShiftableERC1155(
        checkedChainId,
        tokenContractAddress,
        tokenIds,
        tokenAmounts,
        shouldSendToBlessnet,
      );
      if (txRes) return true;
    } catch (error) {
      errorLog(error);
      return false;
    } finally {
      setCheckoutLoadingStatus(null);
    }
  }

  const handleResetMultiTokenApproval = async (tokenContract: HexString) => {
    const response = await resetApprovalForMultiTokens(tokenContract);
    console.log(response);
  };

  // 4. Frontend Button Behavior
  // Display "Approve" if approval is required, then switch to "Bridge" after approval is granted.

  return {
    bridgeTokens,
    bridgeOut,
    bridgeOutNative,
    transferAndCall,
    pendingTxns,
    bridgeFromBless,
    generalAssets: {
      checkoutForERC20,
      checkoutForERC721,
      checkoutForERC1155,
      handleResetMultiTokenApproval,
    },
  };
};

export default useContractMethods;
